import { cn } from '@/lib/utils';

type BooleanToggleCellProps = {
  value: boolean;
  onChange: (value: boolean) => void;
  trueLabel?: string;
  falseLabel?: string;
  disabled?: boolean;
};

/**
 * Full-cell split toggle for a boolean value (two buttons with a sliding
 * emerald/red background). Designed to fill a `relative` table cell such as
 * `EnvVarCellInput`. Labels default to True/False; pass `trueLabel`/`falseLabel`
 * to reuse it elsewhere (e.g. Yes/No).
 */
export const BooleanToggleCell = ({
  value,
  onChange,
  trueLabel = 'True',
  falseLabel = 'False',
  disabled = false
}: BooleanToggleCellProps) => (
  <div className="absolute inset-0 flex font-sans text-xs">
    <div
      className={cn(
        'pointer-events-none absolute inset-y-0 w-1/2 transition-[left] duration-200 [transition-timing-function:var(--ease-out-quart)]',
        value ? 'left-0 bg-emerald-100' : 'left-1/2 bg-red-50'
      )}
    />
    <button
      className={cn(
        'relative z-10 flex flex-1 items-center justify-center transition-colors duration-200',
        value
          ? 'font-medium text-emerald-700'
          : 'text-muted-foreground hover:text-foreground'
      )}
      disabled={disabled}
      onClick={() => onChange(true)}
      type="button"
    >
      {trueLabel}
    </button>
    <div className="relative z-10 w-px bg-gray-200" />
    <button
      className={cn(
        'relative z-10 flex flex-1 items-center justify-center transition-colors duration-200',
        value
          ? 'text-muted-foreground hover:text-foreground'
          : 'font-medium text-red-700'
      )}
      disabled={disabled}
      onClick={() => onChange(false)}
      type="button"
    >
      {falseLabel}
    </button>
  </div>
);
